import React, { useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAuthState } from "@web-src/features/auth/authSlice";
import { PageRoute } from "@web-src/features/app/types";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { EntityGateway } from "@web-src/modules/entities/components/EntityGateway";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider";
import { FullPageError } from "@web-src/modules/common/components/FullPageError";
import { SubscriptionPlanModuleId } from "@jugl-web/rest-api/entities/models/common-types/SubscriptionPlanModuleId";
import { useLinkToOpenAfterLogin } from "@web-src/modules/auth/hooks/useLinkToOpenAfterLogin";
import { PageLoaderFull } from "@jugl-web/ui-components";

const PrivateRoute: React.FC<{
  component: React.ReactNode;
  profileRequired?: boolean;
  entityRequired?: boolean;
  activeSubscriptionRequired?: boolean;
  subscriptionModuleRequired?: SubscriptionPlanModuleId;
  adminRequired?: boolean;
}> = ({
  component,
  profileRequired = true,
  entityRequired = true,
  activeSubscriptionRequired = true,
  subscriptionModuleRequired,
  adminRequired,
}) => {
  const { isAuthenticated } = useSelector(selectAuthState);
  const location = useLocation();
  const [linkToOpenAfterLogin, setLinkToOpenAfterLogin] =
    useLinkToOpenAfterLogin();
  const {
    isInitialized: isEntityInitialized,
    entity,
    isEntitiesFetching,
    reinitialize,
    isInitializationError,
    subscriptionInfo,
    isModuleAvailable,
    moduleRequiredAction,
  } = useEntityProvider();
  const { usersApi } = useRestApiProvider();
  const {
    data: profileData,
    error: profileError,
    isLoading: isProfileLoading,
    isError: isProfileLoadingError,
    refetch: refetchProfileData,
  } = usersApi.useGetUserProfileQuery(
    {},
    {
      skip: !isAuthenticated,
    }
  );
  const hasProfile = !!profileData;

  const handleRetryOnError = useCallback(() => {
    if (isInitializationError) {
      reinitialize();
    }
    if (isProfileLoadingError) {
      refetchProfileData();
    }
  }, [
    isInitializationError,
    isProfileLoadingError,
    refetchProfileData,
    reinitialize,
  ]);

  if (
    (profileRequired &&
      isProfileLoadingError &&
      (profileError as unknown as { status?: number }).status !== 404) ||
    isInitializationError
  ) {
    return <FullPageError onRetry={handleRetryOnError} />;
  }

  if (isAuthenticated === false) {
    if (linkToOpenAfterLogin !== location.pathname) {
      setLinkToOpenAfterLogin(location.pathname);
    }
    return (
      <Navigate
        to={{
          pathname: `/${PageRoute.login}`,
        }}
      />
    );
  }

  if (
    !isAuthenticated ||
    (isProfileLoading && !hasProfile) ||
    (entityRequired && !isEntityInitialized) ||
    (entityRequired && !entity && isEntitiesFetching)
  ) {
    return <PageLoaderFull />;
  }

  if (!hasProfile && profileRequired) {
    return (
      <Navigate
        to={{
          pathname: `/${PageRoute.profileForm}`,
        }}
      />
    );
  }

  if (hasProfile && !profileRequired) {
    return (
      <Navigate
        to={{
          pathname: `/${PageRoute.orgSelect}`,
        }}
      />
    );
  }

  if (entityRequired) {
    if (!entity) {
      return (
        <Navigate
          to={{
            pathname: `/${PageRoute.orgSelect}`,
          }}
        />
      );
    }
    const hasActiveSubscription =
      !!subscriptionInfo && subscriptionInfo.planIsActive;
    if (activeSubscriptionRequired && !hasActiveSubscription) {
      return (
        <Navigate
          to={{ pathname: `/${entity.id}/${PageRoute.noSubscription}` }}
        />
      );
    }
    if (
      subscriptionModuleRequired &&
      !isModuleAvailable(subscriptionModuleRequired)
    ) {
      moduleRequiredAction(subscriptionModuleRequired);
      return (
        <Navigate
          to={{
            pathname: `/${entity.id}/chats`,
          }}
        />
      );
    }
    if (adminRequired && entity.role !== "admin") {
      return (
        <Navigate
          to={{
            pathname: `/${entity.id}/chats`,
          }}
        />
      );
    }
    return <EntityGateway>{component}</EntityGateway>;
  }
  return <>{component}</>;
};

export default PrivateRoute;
